import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { v4 as uuidv4 } from 'uuid'

import { duplicateCampaignBody } from '@iws/theme-schema/campaign'
import type { OfferModel as SharedOffer } from '@iws/theme-schema/types/models/offer'

import encryption from '@/utils/encryption.ts'

import { OfferModel } from '@/http/models/Offer'
import { OfferHttpParams, OfferModel as OfferModelType } from '@/types'

import { useFetchCampaigns } from './useFetchCampaigns'
import { usePreviewCampaign } from './usePreviewCampaign'

// Fetches real totals across all campaigns, not just the current page.
// graph_enc must be encrypted the same way stringifyGraph() encrypts list queries —
// the backend can't decode a plaintext graph string.
const STATS_GRAPH = encryption.encrypt(
  '{total.count,active(status=active).count,inactive(status=inactive).count,archived(status=archived).count}',
  import.meta.env.VITE_GRAPHSQL_SECRET || '0',
)

export const useCampaignsTable = ({ appliesTo }: { appliesTo?: OfferModelType.AppliesTo } = {}) => {
  const navigate = useNavigate()
  const { previewLoading, previewOffer } = usePreviewCampaign()

  // ── Stats ────────────────────────────────────────────────────────
  const [stats, setStats] = useState<OfferModelType.OfferStats>({
    total_count: 0,
    active_count: 0,
    inactive_count: 0,
    archived_count: 0,
  })
  const [statsLoading, setStatsLoading] = useState(false)

  const fetchStats = async () => {
    setStatsLoading(true)
    if (appliesTo) {
      // A stats group takes one condition only, so per-channel counts come from the list's total.
      const count = async (status?: OfferModelType.Status) => {
        const r = await OfferModel.getList({ query: { page: 1, length: 1, applies_to: appliesTo, status } })
        return r.success ? r.data?.count ?? 0 : 0
      }
      const [total_count, active_count, inactive_count, archived_count] = await Promise.all([
        count(), count('active'), count('inactive'), count('archived'),
      ])
      setStats({ total_count, active_count, inactive_count, archived_count })
    } else {
      const response = await OfferModel.getStats({ query: { graph_enc: STATS_GRAPH } })
      if (response.success && response.data?.result) {
        setStats(response.data.result)
      }
    }
    setStatsLoading(false)
  }

  useEffect(() => { fetchStats() }, [])

  // ── List data ────────────────────────────────────────────────────
  const { loading, data, pagination, formValues, formMethods, setPage, setPageSize, refresh } =
    useFetchCampaigns({ sort: null, appliesTo })

  const refreshAll = () => { refresh(); fetchStats() }

  // ── Delete dialog ────────────────────────────────────────────────
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [campaignToDelete, setCampaignToDelete] = useState<OfferModelType.Offer | null>(null)
  const [deleteLoading, setDeleteLoading] = useState(false)

  const handleDeleteClick = (campaign: OfferModelType.Offer) => {
    setCampaignToDelete(campaign)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!campaignToDelete?._id) return
    setDeleteLoading(true)
    const response = await OfferModel.delete({ params: { id: campaignToDelete._id } })
    if (response.success) {
      toast.success('Campaign deleted')
      setDeleteDialogOpen(false)
      setCampaignToDelete(null)
      refreshAll()
    } else {
      toast.error(response.error?.data?.message || 'Failed to delete campaign')
    }
    setDeleteLoading(false)
  }

  // ── Restore ──────────────────────────────────────────────────────
  const handleRestore = async (campaign: OfferModelType.Offer) => {
    const response = await OfferModel.restore({ params: { id: campaign._id } })
    if (response.success) {
      toast.success('Campaign restored')
      refreshAll()
    } else {
      toast.error('Failed to restore campaign')
    }
  }

  // ── Status toggle ────────────────────────────────────────────────
  const toggleStatus = async (campaign: OfferModelType.Offer) => {
    const next = campaign.status === 'active' ? 'inactive' : 'active'
    const response = await OfferModel.update({
      params: { id: campaign._id },
      body: { status: next },
    })
    if (response.success) {
      toast.success(`Campaign ${next === 'active' ? 'activated' : 'paused'}`)
      refreshAll()
    } else {
      toast.error('Failed to update status')
    }
  }

  // ── Duplicate ────────────────────────────────────────────────────
  // The copy is switched off with the same rules, limits and page, and opens in the builder.
  const duplicate = async (campaign: OfferModelType.Offer) => {
    const full = await OfferModel.getById({ params: { id: campaign._id } })
    const source = full.success && full.data?.result ? full.data.result : campaign
    const body: OfferHttpParams.Create['body'] = {
      ...(duplicateCampaignBody(source as unknown as SharedOffer.Offer, uuidv4) as unknown as OfferHttpParams.Create['body']),
      max_total_uses: source.max_total_uses,
      max_uses_per_customer: source.max_uses_per_customer,
    }
    const response = await OfferModel.create({ body })
    if (response.success && response.data?.result) {
      toast.success('Campaign duplicated')
      navigate(`/campaigns/${response.data.result._id}`)
    } else {
      toast.error(response.error?.data?.message || 'Failed to duplicate campaign')
    }
  }

  // ── Navigation ───────────────────────────────────────────────────
  const openCreateBuilder = () => navigate('/campaigns/new')
  const openEditBuilder = (campaign: OfferModelType.Offer) => navigate(`/campaigns/${campaign._id}`)

  return {
    loading,
    data,
    pagination,
    formValues,
    formMethods,
    setPage,
    setPageSize,
    refresh: refreshAll,
    stats,
    statsLoading,
    deleteDialogOpen,
    campaignToDelete,
    deleteLoading,
    setDeleteDialogOpen,
    handleDeleteClick,
    handleDeleteConfirm,
    handleRestore,
    toggleStatus,
    duplicate,
    openCreateBuilder,
    openEditBuilder,
    previewLoading,
    previewOffer,
  }
}
